import React, { useState, useEffect } from 'react';
import api from '../../api/auth';

const StudentGrades = () => {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState('all');

  useEffect(() => {
    fetchGrades();
  }, []);

  const fetchGrades = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/student/grades', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGrades(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const subjects = [...new Set(grades.map((g) => g.subject))];

  const filteredGrades = selectedSubject === 'all'
    ? grades
    : grades.filter((g) => g.subject === selectedSubject);

  const getSubjectAverage = (subject) => {
    const subjectGrades = grades.filter((g) => g.subject === subject);
    if (subjectGrades.length === 0) return null;
    const total = subjectGrades.reduce((acc, g) => acc + parseFloat(g.score) * (g.coefficient || 1), 0);
    const coef = subjectGrades.reduce((acc, g) => acc + (g.coefficient || 1), 0);
    return coef > 0 ? (total / coef).toFixed(2) : null;
  };

  const getGeneralAverage = () => {
    if (grades.length === 0) return null;
    const total = grades.reduce((acc, g) => acc + parseFloat(g.score) * (g.coefficient || 1), 0);
    const coef = grades.reduce((acc, g) => acc + (g.coefficient || 1), 0);
    return coef > 0 ? (total / coef).toFixed(2) : null;
  };

  const getGradeColor = (score) => {
    const s = parseFloat(score);
    if (s >= 16) return 'text-green-600';
    if (s >= 14) return 'text-blue-600';
    if (s >= 12) return 'text-yellow-600';
    if (s >= 10) return 'text-orange-600';
    return 'text-red-600';
  };

  const getAverageColor = (avg) => {
    if (avg === null) return 'text-gray-400';
    const a = parseFloat(avg);
    if (a >= 16) return 'text-green-600';
    if (a >= 14) return 'text-blue-600';
    if (a >= 12) return 'text-yellow-600';
    if (a >= 10) return 'text-orange-600';
    return 'text-red-600';
  };

  const getMention = (avg) => {
    if (avg === null) return '-';
    const a = parseFloat(avg);
    if (a >= 16) return 'Très bien';
    if (a >= 14) return 'Bien';
    if (a >= 12) return 'Assez bien';
    if (a >= 10) return 'Passable';
    return 'Insuffisant';
  };

  if (loading) return <div className="p-6">Chargement...</div>;

  const generalAverage = getGeneralAverage();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">📊 Mes notes et résultats</h1>
        <p className="text-gray-500">Consultez vos notes par matière</p>
      </div>

      {generalAverage !== null && (
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl shadow-md p-6 border-l-4 border-blue-500">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-blue-700 mb-1">📈 Moyenne générale</p>
              <p className={`text-4xl font-bold ${getAverageColor(generalAverage)}`}>
                {generalAverage}/20
              </p>
              <p className="text-sm text-blue-600 mt-1">
                Mention : <strong>{getMention(generalAverage)}</strong>
              </p>
            </div>
            <div className="text-5xl">🎓</div>
          </div>
        </div>
      )}

      {subjects.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {subjects.map((subject) => {
            const avg = getSubjectAverage(subject);
            return (
              <div key={subject} className="bg-white rounded-xl shadow-md p-4 text-center border-l-4 border-[#E67E22]">
                <p className="text-sm text-gray-500">{subject}</p>
                <p className={`text-2xl font-bold ${getAverageColor(avg)}`}>
                  {avg !== null ? `${avg}/20` : '-'}
                </p>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Filtrer par matière
        </label>
        <select
          value={selectedSubject}
          onChange={(e) => setSelectedSubject(e.target.value)}
          className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-[#E67E22]"
        >
          <option value="all">Toutes les matières</option>
          {subjects.map((subject) => (
            <option key={subject} value={subject}>{subject}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-bold text-[#2C3E50]">
            📋 Détail des notes ({filteredGrades.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Matière</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Évaluation</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Note</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Coeff.</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Appréciation</th>
              </tr>
            </thead>
            <tbody>
              {filteredGrades.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    Aucune note enregistrée
                  </td>
                </tr>
              ) : (
                filteredGrades.map((grade, index) => (
                  <tr key={index} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{grade.subject}</td>
                    <td className="py-3 px-4 text-sm">{grade.evaluation_type || 'Devoir'}</td>
                    <td className={`py-3 px-4 font-bold ${getGradeColor(grade.score)}`}>
                      {parseFloat(grade.score).toFixed(2)}/20
                    </td>
                    <td className="py-3 px-4 text-sm">{grade.coefficient}</td>
                    <td className="py-3 px-4 text-sm">{grade.date}</td>
                    <td className="py-3 px-4 text-sm text-gray-500">
                      {grade.appreciation || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StudentGrades;